/**
 * Password validation utility functions
 */

/**
 * Validates password strength based on security requirements
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validatePasswordStrength = (password) => {
  // Initialize validation result
  const result = {
    isValid: true,
    message: '',
    details: []
  };

  // Check minimum length (8 characters)
  if (password.length < 8) {
    result.isValid = false;
    result.details.push('Password must be at least 8 characters long');
  }

  // Check for uppercase letters
  if (!/[A-Z]/.test(password)) {
    result.isValid = false;
    result.details.push('Password must contain at least one uppercase letter');
  }

  // Check for lowercase letters
  if (!/[a-z]/.test(password)) {
    result.isValid = false;
    result.details.push('Password must contain at least one lowercase letter');
  }

  // Check for numbers
  if (!/\d/.test(password)) {
    result.isValid = false;
    result.details.push('Password must contain at least one number');
  }

  // Check for special characters
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    result.isValid = false;
    result.details.push('Password must contain at least one special character');
  }

  // Compile error message if validation failed
  if (!result.isValid) {
    result.message = 'Password does not meet security requirements:';
  }

  return result;
};

/**
 * Calculates password strength score (0-100)
 * @param {string} password - The password to evaluate
 * @returns {number} - Strength score between 0-100
 */
export const calculatePasswordStrength = (password) => {
  if (!password) return 0;

  let score = 0;

  // Length contribution (up to 25 points)
  score += Math.min(25, password.length * 2);

  // Character variety contribution
  if (/[A-Z]/.test(password)) score += 10; // Uppercase
  if (/[a-z]/.test(password)) score += 10; // Lowercase
  if (/\d/.test(password)) score += 10;    // Numbers
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 15; // Special chars

  // Variety of character types (up to 20 points)
  const charTypes = [/[A-Z]/, /[a-z]/, /\d/, /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/];
  const typesUsed = charTypes.filter(type => type.test(password)).length;
  score += typesUsed * 5;

  // Penalize repetitive patterns
  if (/(.)\1{2,}/.test(password)) score -= 10; // Same character repeated 3+ times

  // Ensure score is between 0-100
  return Math.max(0, Math.min(100, score));
};

/**
 * Gets a descriptive strength label based on score
 * @param {number} score - Password strength score (0-100)
 * @returns {string} - Descriptive label
 */
export const getPasswordStrengthLabel = (score) => {
  if (score < 30) return 'Very Weak';
  if (score < 50) return 'Weak';
  if (score < 70) return 'Moderate';
  if (score < 90) return 'Strong';
  return 'Very Strong';
};
